export const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
export const DEFAULT_RETRY_DELAYS = [300, 800, 1500];

export const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const isAbsoluteUrl = value => /^https?:\/\//i.test(value || '');

const toSearchString = params => {
  if (!params) return '';
  if (typeof params === 'string') return params;
  if (params instanceof URLSearchParams) {
    return params.toString();
  }
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    search.append(key, String(value));
  });
  return search.toString();
};

const buildUrl = (endpoint, params, baseOverride) => {
  const base = baseOverride === undefined || baseOverride === null
    ? API_BASE
    : String(baseOverride).replace(/\/$/, '');
  const search = toSearchString(params);
  if (isAbsoluteUrl(endpoint)) {
    if (search) {
      return endpoint.includes('?') ? `${endpoint}&${search}` : `${endpoint}?${search}`;
    }
    return endpoint;
  }
  const normalizedPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const prefix = base ? `${base}${normalizedPath}` : normalizedPath;
  if (!search) {
    return prefix;
  }
  return prefix.includes('?') ? `${prefix}&${search}` : `${prefix}?${search}`;
};

const safeInvoke = (fn, payload) => {
  if (typeof fn !== 'function') return;
  try {
    fn(payload);
  } catch (err) {
    console.warn('fetchWithRetry onRetry callback failed', err);
  }
};

export async function fetchWithRetry(endpoint, options = {}) {
  const {
    params,
    method = 'GET',
    headers,
    body,
    credentials = 'include',
    retryDelays = DEFAULT_RETRY_DELAYS,
    signal,
    base,
    onRetry,
    cache,
    mode,
  } = options;

  const delays = Array.isArray(retryDelays) ? retryDelays : DEFAULT_RETRY_DELAYS;
  const totalAttempts = (delays.length || 0) + 1;
  const url = buildUrl(endpoint, params, base);
  const fetchOptions = {
    method,
    credentials,
    signal,
  };

  if (headers) {
    fetchOptions.headers = { ...headers };
  }
  if (body !== undefined) {
    fetchOptions.body = body;
  }
  if (cache !== undefined) {
    fetchOptions.cache = cache;
  }
  if (mode !== undefined) {
    fetchOptions.mode = mode;
  }

  let attempt = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fetch(url, fetchOptions);
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw err;
      }
      if (!(err instanceof TypeError)) {
        throw err;
      }
      if (!delays.length || attempt >= delays.length) {
        throw err;
      }
      const delayMs = delays[attempt];
      attempt += 1;
      safeInvoke(onRetry, { attempt, total: totalAttempts, delay: delayMs, error: err });
      if (delayMs > 0) {
        await delay(delayMs);
      }
    }
  }
}
